import { useState } from "react";
import api from "../../lib/api";
import { itDate } from "../../lib/constants";
import { Button } from "../ui/button";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import FieldsDialog from "./FieldsDialog";
import { BASES, COST_TYPES, label, costText, discountText, effectivePct, today } from "./meta";

const DISC_FIELDS = [
  { k: "kind", label: "Riferito a", type: "select", options: [["categoria", "Categoria di prodotti"], ["prodotto", "Singolo prodotto"]], half: true },
  { k: "product", label: "Nome prodotto o categoria *", half: true, ph: "es. Tende a bracci" },
  { k: "discount_pct", label: "Sconto principale %", type: "number", half: true, ph: "es. 50" },
  { k: "cascade", label: "Sconti successivi a cascata", half: true, ph: "es. 10+5", hint: "Separati da +, applicati uno dopo l'altro" },
  { k: "start_date", label: "Valido dal", type: "date", half: true },
  { k: "expiry", label: "Scadenza (facoltativa)", type: "date", half: true },
  { k: "conditions", label: "Condizioni particolari", ph: "es. ordine minimo 20 pezzi" },
  { k: "notes", label: "Note", type: "textarea" },
];

const COST_FIELDS = (type) => [
  { k: "type", label: "Tipologia", type: "select", options: COST_TYPES, half: true },
  { k: "name", label: "Nome della voce *", half: true, ph: type === "trasporto" ? "es. Trasporto pergole" : "es. Imballaggio speciale" },
  { k: "category", label: "Categoria prodotto (facoltativa)", half: true, ph: "es. Pergole" },
  { k: "basis", label: "Come si calcola", type: "select", options: BASES, half: true },
  { k: "amount", label: "Importo € o percentuale %", type: "number", half: true, show: (f) => f.basis !== "personalizzato" },
  { k: "amount", label: "Importo indicativo € (facoltativo)", type: "number", half: true, show: (f) => f.basis === "personalizzato" },
  { k: "unit", label: "Unità di misura", half: true, ph: "es. m², ml, kg", show: (f) => f.basis === "unita" },
  { k: "free_over", label: "Gratuito oltre (€ di ordine netto)", type: "number", half: true, hint: "Lascia vuoto se non previsto" },
  { k: "notes", label: "Note / condizione", type: "textarea" },
];

function useCrud(endpoint, onChanged) {
  const [dlg, setDlg] = useState(null);
  const run = async (fn, msg) => { try { await fn(); toast.success(msg); setDlg(null); onChanged(); } catch (e) { toast.error(e?.response?.data?.detail || "Operazione non riuscita"); } };
  const save = (form) => run(() => (form.id ? api.put(`${endpoint}/${form.id}`, form) : api.post(endpoint, form)), "Voce salvata");
  const del = () => run(() => api.delete(`${endpoint}/${dlg.id}`), "Voce eliminata");
  return { dlg, setDlg, save, del };
}

const Row = ({ testid, onClick, title, sub, right, muted }) => (
  <button data-testid={testid} onClick={onClick} className={`w-full text-left p-3 rounded-lg bg-slate-50 hover:bg-accent transition-colors flex items-center gap-3 ${muted ? "opacity-60" : ""}`}>
    <div className="min-w-0 flex-1"><p className="text-sm font-medium truncate">{title}</p>{sub && <p className="text-xs text-muted-foreground truncate">{sub}</p>}</div>
    {right && <span className="font-head font-bold text-sm text-brand whitespace-nowrap">{right}</span>}
  </button>
);

export function DiscountsSection({ supplierId, items, onChanged }) {
  const c = useCrud("/discounts", onChanged);
  const open = (d) => c.setDlg(d ? { ...d, cascade: (d.cascade || []).join("+") } : { supplier_id: supplierId, kind: "categoria", start_date: today() });
  return (
    <div className="card-soft p-4" data-testid="sec-sconti">
      <div className="flex items-center justify-between mb-3"><p className="text-sm text-muted-foreground">{items.length} sconti registrati</p>
        <Button size="sm" className="bg-brand hover:bg-[#A8472F] text-white" data-testid="disc-add" onClick={() => open(null)}><Plus size={15} className="mr-1" /> Aggiungi sconto</Button></div>
      <div className="space-y-2">
        {items.length === 0 && <p className="text-sm text-muted-foreground">Nessuno sconto.</p>}
        {items.map((d) => {
          const exp = d.expiry && d.expiry < today();
          return <Row key={d.id} testid={`disc-row-${d.id}`} onClick={() => open(d)} muted={exp} title={`${d.product}${d.kind === "prodotto" ? " (prodotto)" : ""}`}
            sub={[d.start_date && `dal ${itDate(d.start_date)}`, d.expiry && `${exp ? "scaduto il" : "fino al"} ${itDate(d.expiry)}`, d.conditions].filter(Boolean).join(" · ")}
            right={`${discountText(d)}${(d.cascade || []).length ? ` = ${effectivePct(d)}` : ""}`} />;
        })}
      </div>
      <FieldsDialog open={!!c.dlg} testid="disc-dialog" title={c.dlg?.id ? "Modifica sconto" : "Nuovo sconto"} fields={DISC_FIELDS} initial={c.dlg}
        onClose={() => c.setDlg(null)} onSave={c.save} onDelete={c.dlg?.id ? c.del : null} />
    </div>
  );
}

export function CostsSection({ supplierId, items, types, onChanged, testid }) {
  const c = useCrud("/supplier-costs", onChanged);
  const list = items.filter((x) => types.includes(x.type));
  const def = types[0];
  return (
    <div className="card-soft p-4" data-testid={testid}>
      <div className="flex items-center justify-between mb-3"><p className="text-sm text-muted-foreground">{list.length} voci</p>
        <Button size="sm" className="bg-brand hover:bg-[#A8472F] text-white" data-testid={`${testid}-add`} onClick={() => c.setDlg({ supplier_id: supplierId, type: def, basis: "ordine" })}><Plus size={15} className="mr-1" /> Aggiungi voce</Button></div>
      <div className="space-y-2">
        {list.length === 0 && <p className="text-sm text-muted-foreground">Nessuna voce.</p>}
        {list.map((x) => <Row key={x.id} testid={`cost-row-${x.id}`} onClick={() => c.setDlg({ ...x })} title={x.name}
          sub={[types.length > 1 && label(COST_TYPES, x.type), x.category, label(BASES, x.basis), x.notes].filter(Boolean).join(" · ")} right={costText(x)} />)}
      </div>
      <FieldsDialog open={!!c.dlg} testid="cost-dialog" title={c.dlg?.id ? "Modifica voce di costo" : "Nuova voce di costo"} fields={COST_FIELDS(def)} initial={c.dlg}
        onClose={() => c.setDlg(null)} onSave={c.save} onDelete={c.dlg?.id ? c.del : null} />
    </div>
  );
}
